// PersonalReconciliation.jsx
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Form, Button } from "react-bootstrap";
import {
  getPersonalExpenseData,
  personalReconciliation,
} from "../Api/Reconciliation";
import { getAllCompanyCodes } from "../Api/CompanyLocationMaster";
import { searchReconciliationDocument } from "../Api/Reconciliation";
import { useAuth } from "../Context/AuthContext";

/**
 * PersonalReconciliation
 *
 * - Adds a location search box that shows a dropdown of suggestions (Nominatim).
 * - Selecting a suggestion stores address, lat, lon in the payload sent on submit.
 * - Includes keyboard navigation (ArrowUp/ArrowDown/Enter) and click-outside to close.
 *
 * NOTE: Replace NOMINATIM_EMAIL with a contact email as recommended by Nominatim.
 */

const NOMINATIM_SEARCH_URL = "https://nominatim.openstreetmap.org/search";
const NOMINATIM_EMAIL = "replace-with-your-email@example.com"; // <- set your contact email

const PersonalReconciliation = () => {
  const location = useLocation();
  const { statement } = location.state || {};
  const navigate = useNavigate();
  const { monthId, houseBankId } = useParams();
  const { user } = useAuth();

  // Data lists
  const [expenseHeads, setExpenseHeads] = useState([]);
  const [head2Options, setHead2Options] = useState([]);
  const [head3Options, setHead3Options] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);

  // Form data (main fields)
  const [formData, setFormData] = useState({
    // transferType: "",
    transferFrom: "",
    transferFromCompanyCode: "",
    transferTo: "",
    transferToCompanyCode: "",
    amount: statement?.transactionAmount || "",
    currency: "INR",
    remarks: "",
    expenseType: "",
    head1: "",
    head2: "",
    head3: "",
    // location fields will be set when user selects from dropdown
    locationAddress: "",
    locationLat: "",
    locationLon: "",
    createdBy:user?.userId,
  });

  // Location search states
  const [locationQuery, setLocationQuery] = useState("");
  const [locationResults, setLocationResults] = useState([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1); // for keyboard nav
  const [showDropdown, setShowDropdown] = useState(false);

  // refs for aborting fetch and click-outside
  const locAbortRef = useRef(null);
  const locDebounceRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch Expense Heads
  useEffect(() => {
    const fetchExpenseHeads = async () => {
      try {
        const data = await getPersonalExpenseData();
        setExpenseHeads(data || []);
      } catch (error) {
        console.error("Error fetching personal expense data:", error);
      }
    };
    fetchExpenseHeads();
  }, []);

  // Fetch Company Codes
  useEffect(() => {
    const fetchCompanyCodes = async () => {
      try {
        const data = await getAllCompanyCodes();
        setCompanyCodes(data || []);
      } catch (error) {
        console.error("Error fetching company codes:", error);
      }
    };
    fetchCompanyCodes();
  }, []);

  // When Head1 changes → fetch Head2 list
  // useEffect(() => {
  //   const fetchHead2 = async () => {
  //     if (formData.head1) {
  //       try {
  //         const data = await searchReconciliationDocument(formData.head1);
  //         setHead2Options(data || []);
  //         setHead3Options([]);
  //         setFormData((prev) => ({ ...prev, head2: "", head3: "" }));
  //       } catch (error) {
  //         console.error("Error fetching head2 data:", error);
  //       }
  //     }
  //   };
  //   fetchHead2();
  // }, [formData.head1]);

  // When Head2 changes → filter Head3 list
  useEffect(() => {
    if (formData.head2 && head2Options.length > 0) {
      const filteredHead3 = head2Options.filter(
        (item) => item.head2 === formData.head2
      );
      setHead3Options(filteredHead3);
      setFormData((prev) => ({ ...prev, head3: "" }));
    }
  }, [formData.head2, head2Options]);

  // Debounced Nominatim search (shows dropdown results)
  useEffect(() => {
    const q = (locationQuery || "").trim();
    if (!q) {
      setLocationResults([]);
      setShowDropdown(false);
      setHighlightIndex(-1);
      // abort any running request
      if (locAbortRef.current) {
        locAbortRef.current.abort();
        locAbortRef.current = null;
      }
      return;
    }

    // clear previous debounce
    if (locDebounceRef.current) clearTimeout(locDebounceRef.current);

    locDebounceRef.current = setTimeout(() => {
      // abort previous in-flight fetch
      if (locAbortRef.current) {
        locAbortRef.current.abort();
        locAbortRef.current = null;
      }

      const controller = new AbortController();
      locAbortRef.current = controller;
      setIsSearchingLocation(true);

      const params = new URLSearchParams({
        q,
        format: "json",
        limit: "6",
        addressdetails: "1",
        "accept-language": "en",
        email: NOMINATIM_EMAIL,
      });

      fetch(`${NOMINATIM_SEARCH_URL}?${params.toString()}`, {
        signal: controller.signal,
      })
        .then(async (res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const json = await res.json();
          setLocationResults(Array.isArray(json) ? json : []);
          setShowDropdown(true);
          setHighlightIndex(-1);
        })
        .catch((err) => {
          if (err.name === "AbortError") return;
          console.error("Location search error:", err);
        })
        .finally(() => {
          setIsSearchingLocation(false);
        });
    }, 400); // debounce

    return () => {
      if (locDebounceRef.current) clearTimeout(locDebounceRef.current);
    };
  }, [locationQuery]);

  // Click outside handler to close dropdown
  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setShowDropdown(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  // Keyboard navigation for dropdown
  const onLocationKeyDown = (e) => {
    if (!showDropdown || locationResults.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < locationResults.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : locationResults.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      const idx = highlightIndex >= 0 ? highlightIndex : 0;
      const selected = locationResults[idx];
      if (selected) {
        handleSelectLocation(selected);
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
      setHighlightIndex(-1);
    }
  };

  const handleSelectLocation = (loc) => {
    if (!loc) return;
    setFormData((prev) => ({
      ...prev,
      locationAddress: loc.display_name || "",
      locationLat: loc.lat || "",
      locationLon: loc.lon || "",
    }));
    setLocationQuery(loc.display_name || "");
    setLocationResults([]);
    setShowDropdown(false);
    setHighlightIndex(-1);
    // move focus back to next form control if desired
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!statement) {
      alert("No statement selected.");
      return;
    }

    const payload = {
      transferFromCompanyCode: null,
      transferToCompanyCode: null,
      statementId: statement.statementId,
      docfy: new Date().getFullYear().toString(),
      recoAmount: formData.amount,
      recoCurrency: formData.currency,
      remarks: formData.remarks,
      head1: formData.head1,
      head2: formData.head2,
      head3: formData.head3,
      // document_Type: formData.transferType === "I2" ? "I2" : "I4",
      organisationId: user?.organisationId || null,
      // location fields included in payload (null if not selected)
      locationAddress: formData.locationAddress || null,
      locationLat: formData.locationLat || null,
      locationLon: formData.locationLon || null,
      createdBy: user?.userId || null,
    };

    console.log("Payload for Personal Reconciliation:", payload);

    try {
      const response = await personalReconciliation(payload);
      console.log("✅ Personal Reconciliation Success:", response);
      alert(
        `${response.message}\nPersonal Reconciliation ID: ${response.personalReconciliationId}`
      );
      navigate(`/manual-Bank-reconciliation/${monthId}/${houseBankId}`);
    } catch (error) {
      console.error("❌ Error submitting Personal Reconciliation:", error);
      alert("Failed to submit Personal Reconciliation");
    }
  };
  const uniqueHead1 = [...new Set(expenseHeads.map(item => item.head1))]
  .filter(Boolean)
  .sort((a, b) => a.localeCompare(b));

useEffect(() => {
  if (!formData.head1) {
    setHead2Options([]);
    return;
  }

  const uniqueHead2 = [
    ...new Set(
      expenseHeads
        .filter(item => item.head1 === formData.head1)
        .map(item => item.head2)
    ),
  ]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  setHead2Options(uniqueHead2);
  setFormData(prev => ({ ...prev, head2: "", head3: "" }));
}, [formData.head1, expenseHeads]);


  useEffect(() => {
  if (!formData.head1 || !formData.head2) {
    setHead3Options([]);
    return;
  }

  const uniqueHead3 = [
    ...new Set(
      expenseHeads
        .filter(
          item =>
            item.head1 === formData.head1 &&
            item.head2 === formData.head2
        )
        .map(item => item.head3)
    ),
  ]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  setHead3Options(uniqueHead3);
  setFormData(prev => ({ ...prev, head3: "" }));
}, [formData.head2, formData.head1, expenseHeads]);



  return (
    <div className="container mt-4" ref={containerRef}>
      <h4>Personal Reconciliation</h4>

      {statement ? (
        <div className="card mt-3">
          <div className="card-header">
            <h5>Statement Details</h5>
          </div>

          <div className="card-body">
            <table className="table table-bordered">
              <tbody>
                <tr>
                  <th>Statement ID</th>
                  <td>{statement.statementId}</td>
                </tr>
                <tr>
                  <th>Transaction ID</th>
                  <td>{statement.transactionId}</td>
                </tr>
                <tr>
                  <th>Narration</th>
                  <td>{statement.narration}</td>
                </tr>
                <tr>
                  <th>Bank Name</th>
                  <td>{statement.bankName}</td>
                </tr>
                <tr>
                  <th>Account Number</th>
                  <td>{statement.accountNumber}</td>
                </tr>
                <tr>
                  <th>Amount</th>
                  <td>{statement.transactionAmount}</td>
                </tr>
                <tr>
                  <th>Transaction Date</th>
                  <td>
                    {new Date(statement.transactionPostedDate).toLocaleDateString()}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Location search + dropdown */}
            {/* <Form.Group className="mb-3 position-relative">
              <Form.Label>Search Location</Form.Label>
              <Form.Control
                type="text"
                ref={inputRef}
                value={locationQuery}
                onChange={(e) => {
                  setLocationQuery(e.target.value);
                }}
                onKeyDown={onLocationKeyDown}
                placeholder="Type address or place (select from dropdown)"
                aria-autocomplete="list"
                aria-expanded={showDropdown}
                aria-controls="location-results"
              />

              {isSearchingLocation && (
                <div className="small text-muted mt-1">Searching…</div>
              )}

              {showDropdown && locationResults.length > 0 && (
                <ul
                  id="location-results"
                  className="list-group position-absolute w-100"
                  style={{ zIndex: 1200, maxHeight: 260, overflowY: "auto" }}
                >
                  {locationResults.map((loc, idx) => {
                    const isActive = idx === highlightIndex;
                    return (
                      <li
                        key={loc.place_id || `${loc.lat}-${loc.lon}-${idx}`}
                        role="option"
                        aria-selected={isActive}
                        className={`list-group-item list-group-item-action ${
                          isActive ? "active" : ""
                        }`}
                        style={{ cursor: "pointer" }}
                        onMouseEnter={() => setHighlightIndex(idx)}
                        onMouseLeave={() => setHighlightIndex(-1)}
                        onClick={() => handleSelectLocation(loc)}
                      >
                        <div style={{ fontSize: 13 }}>{loc.display_name}</div>
                        <div style={{ fontSize: 11, color: "#666" }}>
                          lat: {loc.lat} · lon: {loc.lon}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Form.Group> */}

            {/* The main form */}
            <Form onSubmit={handleSubmit}>
              <h5 className="mt-4">Personal Expense Details</h5>

              {/* <Form.Group className="mb-3">
                <Form.Label>Transfer Type</Form.Label>
                <Form.Select
                  name="transferType"
                  value={formData.transferType}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Select --</option>
                  <option value="I4">I4 - Internal</option>
                  <option value="I2">I2 - External</option>
                </Form.Select>
              </Form.Group> */}

              <Form.Group className="mb-3">
                <Form.Label>Head1</Form.Label>
               <Form.Select
  name="head1"
  value={formData.head1}
  onChange={handleChange}
  required
>
  <option value="">-- Select Head1 --</option>
  {uniqueHead1.map((head1) => (
    <option key={head1} value={head1}>
      {head1}
    </option>
  ))}
</Form.Select>

              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Head2</Form.Label>
                <Form.Select
  name="head2"
  value={formData.head2}
  onChange={handleChange}
  required
  disabled={!formData.head1}
>
  <option value="">-- Select Head2 --</option>
  {head2Options.map((head2) => (
    <option key={head2} value={head2}>
      {head2}
    </option>
  ))}
</Form.Select>

              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Head3</Form.Label>
               <Form.Select
  name="head3"
  value={formData.head3}
  onChange={handleChange}
  required
  disabled={!formData.head2}
>
  <option value="">-- Select Head3 --</option>
  {head3Options.map((head3) => (
    <option key={head3} value={head3}>
      {head3}
    </option>
  ))}
</Form.Select>

              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Amount</Form.Label>
                <Form.Control
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Currency</Form.Label>
                <Form.Select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  required
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                />
              </Form.Group>
                    {/* Location search + dropdown */}
            <Form.Group className="mb-3 position-relative">
              <Form.Label>Search Location</Form.Label>
              <Form.Control
                type="text"
                ref={inputRef}
                value={locationQuery}
                onChange={(e) => {
                  setLocationQuery(e.target.value);
                }}
                onKeyDown={onLocationKeyDown}
                placeholder="Type address or place (select from dropdown)"
                aria-autocomplete="list"
                aria-expanded={showDropdown}
                aria-controls="location-results"
              />

              {isSearchingLocation && (
                <div className="small text-muted mt-1">Searching…</div>
              )}

              {showDropdown && locationResults.length > 0 && (
                <ul
                  id="location-results"
                  className="list-group position-absolute w-100"
                  style={{ zIndex: 1200, maxHeight: 260, overflowY: "auto" }}
                >
                  {locationResults.map((loc, idx) => {
                    const isActive = idx === highlightIndex;
                    return (
                      <li
                        key={loc.place_id || `${loc.lat}-${loc.lon}-${idx}`}
                        role="option"
                        aria-selected={isActive}
                        className={`list-group-item list-group-item-action ${
                          isActive ? "active" : ""
                        }`}
                        style={{ cursor: "pointer" }}
                        onMouseEnter={() => setHighlightIndex(idx)}
                        onMouseLeave={() => setHighlightIndex(-1)}
                        onClick={() => handleSelectLocation(loc)}
                      >
                        <div style={{ fontSize: 13 }}>{loc.display_name}</div>
                        {/* <div style={{ fontSize: 11, color: "#666" }}>
                          lat: {loc.lat} · lon: {loc.lon}
                        </div> */}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Form.Group>
              <Button variant="primary" type="submit">
                Submit Personal Expense
              </Button>
           
            </Form>

            {/* <div className="mt-4 text-muted small">
              Location suggestions powered by OpenStreetMap / Nominatim. Do not
              exceed public instance rate limits; add a contact email or self-host for
              higher volume.
            </div> */}
          </div>
        </div>
      ) : (
        <div className="alert alert-warning">
          No statement data available. Please go back and select a statement.
        </div>
      )}
    </div>
  );
};

export default PersonalReconciliation;
